import { useEffect, useRef, useState } from 'react';
import { API_URL } from 'config/app.config';

const UPLOAD_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseUploadKey (url: string): string | null {
  if (!url) return null;
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url.startsWith('/') ? url : `/${url}`;
  }
  const segment = pathname.split('/').filter(Boolean).pop() ?? '';
  if (!UPLOAD_UUID_RE.test(segment)) return null;
  return segment.toLowerCase();
}

export function cdnDisplayUrl (src: string, width?: number): string {
  if (!src || !width || !API_URL) return src;
  if (!parseUploadKey(src)) return src;
  const w = Math.max(1, Math.round(width));
  const base = API_URL.replace(/\/$/, '');
  return `${base}/upload/thumbnail?w=${w}&url=${encodeURIComponent(src)}`;
}

export function useCdnImageSrc (
  original: string | undefined,
  width?: number,
): {
  src: string | undefined;
  onError: () => boolean;
} {
  const [useOriginal, setUseOriginal] = useState(false);
  const useOriginalRef = useRef(false);

  useEffect(() => {
    useOriginalRef.current = false;
    setUseOriginal(false);
  }, [original, width]);

  const src = original
    ? useOriginal
      ? original
      : cdnDisplayUrl(original, width)
    : undefined;

  const onError = (): boolean => {
    if (!original) return true;
    const thumb = cdnDisplayUrl(original, width);
    if (!useOriginalRef.current && thumb !== original) {
      useOriginalRef.current = true;
      setUseOriginal(true);
      return false;
    }
    return true;
  };

  return { src, onError };
}
